import { GoalFormModal } from "../components/ChildHero.jsx";
import { actingAdult, actingAdultHere} from "../lib/adults.js";
import { CollapsibleSection } from "../components/ChildView.jsx";
import { CoinIcon, EditPencilIcon, Icon, ImageLightbox, PremiumChip, VaultIcon, uploadResizedImage } from "../components/Common.jsx";
import { uid } from "../lib/constants.js";
import { t as ct } from "../lib/content.js";
import { imageSrc } from "../lib/origin.js";
import { getDeviceId, getActiveAdultId, getDeviceOwnerAdultId, getHouseholdCode} from "../lib/api.js";
import { currentHabitStreak, goalPricePence, goalProgress, goalTargetTokens, habitWeekProgress, habitsForChild, isHabitDayComplete, logSetting, raiseHabitReward, redeemSourceOptions, resolveRedeemSplit, todayStr, tokensToPence, weekStartOf } from "../lib/utils.js";

// LB-343 — who is doing this; see the twin in SettingsTab.jsx for why it reads the code
// rather than taking it as a prop.
const actor = (s) => actingAdultHere(s, getActiveAdultId, getDeviceOwnerAdultId, getHouseholdCode());
const { useState } = React;

// The three sections C1 extracted out of AdjustTab so both AdjustTab (normal Parent Mode)
// and SetupWizard (step 4) can mount them directly — see
// docs/../claude-code-task-c1-wizard-decoupling.md. Each keeps its own state slice and
// handlers, moved verbatim from AdjustTab; `open`/`onToggle` are optional CONTROLLED props
// (CollapsibleSection treats them as uncontrolled when left undefined), letting the wizard
// drive progressive reveal without AdjustTab needing to know about it.

// onComplete: set only by the Kids quick-action pop-up, which closes once a goal is saved (Alan, 22 Sep).
export function GoalsSection({ child, state, update, addTransaction, code, open, onToggle, compactClosed, variant , rowTestId, onComplete }) {
  const rate = state.settings?.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const [goalFormOpen, setGoalFormOpen] = useState(false);
  const [editingGoalId, setEditingGoalId] = useState(null);
  const [enlargedGoalImage, setEnlargedGoalImage] = useState(null);
  const [goalTitle, setGoalTitle] = useState("");
  const [goalTarget, setGoalTarget] = useState("");
  // LB-355: the price as entered, in pence, so a goal typed in pounds is stored exactly.
  const [goalPence, setGoalPence] = useState(null);
  const [goalImage, setGoalImage] = useState(null);
  const [goalImageError, setGoalImageError] = useState("");
  const [goalImageUploading, setGoalImageUploading] = useState(false);
  const [goalRequirePhoto, setGoalRequirePhoto] = useState(false);
  // Which full source a parent has explicitly picked for a given goal's redemption, keyed by
  // goal id — only consulted when both balances could independently cover the whole amount
  // (see the shared redeemSourceOptions/resolveRedeemSplit helpers near goalProgress).
  const [payFromOverride, setPayFromOverride] = useState({});

  function startAddGoal() { setGoalFormOpen(true); setEditingGoalId(null); setGoalTitle(""); setGoalTarget(""); setGoalPence(null); setGoalImage(null); setGoalImageError(""); setGoalRequirePhoto(false); }
  function startEditGoal(g) { setGoalFormOpen(true); setEditingGoalId(g.id); setGoalTitle(g.title); setGoalTarget(String(goalTargetTokens(g, rate))); setGoalPence(goalPricePence(g, rate)); setGoalImage(g.image || null); setGoalImageError(""); setGoalRequirePhoto(!!g.requirePhoto); }
  async function handleGoalPhoto(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setGoalImageError(""); setGoalImageUploading(true);
    try {
      const url = await uploadResizedImage(code, file, 400, 0.85);
      setGoalImage(url);
    } catch (err) {
      setGoalImageError(err.message || "Couldn't use that photo.");
    } finally {
      setGoalImageUploading(false);
    }
  }
  function saveGoal() {
    const t = parseInt(goalTarget, 10);
    if (!goalTitle.trim() || !t || t <= 0) return;
    // LB-355: stored as money however it was entered.
    const targetPence = goalPence > 0 ? goalPence : tokensToPence(t, rate);
    update((s) => {
      const c = s.profiles.find((p) => p.id === child.id);
      if (!c.goals) c.goals = [];
      if (editingGoalId) {
        c.goals = c.goals.map((g) => { if (g.id !== editingGoalId) return g; const { targetTokens, ...rest } = g; void targetTokens; return { ...rest, title: goalTitle.trim(), targetPence, image: goalImage || null, requirePhoto: goalRequirePhoto }; });
      } else {
        c.goals.push({ id: uid(), title: goalTitle.trim(), targetPence, image: goalImage || null, requirePhoto: goalRequirePhoto });
      }
      return s;
    });
    setGoalTitle(""); setGoalTarget(""); setGoalImage(null); setGoalImageError(""); setGoalFormOpen(false); setEditingGoalId(null);
    if (onComplete) onComplete();
  }
  function removeGoal(goalId) {
    update((s) => { const c = s.profiles.find((p) => p.id === child.id); c.goals = (c.goals || []).filter((g) => g.id !== goalId); return s; });
  }
  // Mirrors RequestsTab's approve() logic exactly for goal_redeem, so a parent redeeming a
  // goal directly (bypassing a kid's request) behaves identically: same source rules, same
  // auto-split when forced, and any redemption drawing on the Savings Vault gets tagged as a
  // vault transaction too, not just genuinely mixed ones.
  function redeemGoal(goal) {
    const chosen = payFromOverride[goal.id] === "vault" ? "vault" : "token";
    const { tokenPart, vaultPart } = resolveRedeemSplit(child, goalTargetTokens(goal, rate), rate, chosen);
    const vaultPence = tokensToPence(vaultPart, rate);
    if (tokenPart > 0) addTransaction(child.id, tokenPart, `Redeemed goal: ${goal.title}`, "spend", "token");
    if (vaultPart > 0) {
      addTransaction(child.id, vaultPence, `Redeemed goal: ${goal.title}`, "spend", "vault");
      // Any redemption drawing on the Savings Vault — mixed or vault-only — is real money
      // leaving it just like a transfer would, so tag the spend row just logged above as a
      // "from Savings Vault" vault transaction too, alongside its regular appearance in
      // Recent activity.
      update((s) => {
        if (s.transactions[0]) { s.transactions[0].vaultTransfer = "from"; s.transactions[0].tokens = vaultPart; s.transactions[0].pence = vaultPence; }
        return s;
      });
    }
    removeGoal(goal.id);
    setPayFromOverride((o) => { if (!(goal.id in o)) return o; const next = { ...o }; delete next[goal.id]; return next; });
  }

  return (
    <>
      <CollapsibleSection
        title={ct("kid_detail.goals_title", "Goals")}
      testid={rowTestId}
      icon={variant === "row" ? <Icon id="ic-goals" size={18} hue="teal" /> : undefined}
        open={open}
        onToggle={onToggle}
        compactClosed={compactClosed}
        variant={variant}
        summary={variant === "row" ? (() => {
          // R-C row summary — one glanceable line while collapsed (11d): the top goal's
          // progress via the same goalProgress helper the cards below use, or a nudge.
          const goals = child.goals || [];
          if (goals.length === 0) return <p className="text-xs text-slate-500">{ct("kid_detail.goals_row_empty", "No goals yet")}</p>;
          const top = goals[0];
          const pct = goalProgress(child, goalTargetTokens(top, rate), rate).pct;
          return <p className="text-xs text-slate-500">{ct("kid_detail.goals_row_summary", "{title} · {pct}% saved{more}", { title: top.title, pct, more: goals.length > 1 ? ` · +${goals.length - 1} more` : "" })}</p>;
        })() : (
          <div>
            <p className="text-base text-slate-500">Create goals for {child.name} to save their tokens towards. Goals can be big or small, the key is that they motivate them.</p>
            {(child.goals || []).length === 0 ? (
              <button className="mt-1.5 bg-pill text-navy font-bold text-sm rounded-lg px-3 py-1.5" onClick={(e) => { e.stopPropagation(); startAddGoal(); }}>{ct("kid_detail.add_goal_button", "+ Add goal")}</button>
            ) : (
              <p className="text-xs text-slate-400 mt-0.5">{`${(child.goals || []).length} goal${(child.goals || []).length === 1 ? "" : "s"}`}</p>
            )}
          </div>
        )}
      >
        <div className="flex flex-col gap-3">
          {(child.goals || []).map((g) => {
            const gp = goalProgress(child, goalTargetTokens(g, rate), rate);
            const reached = gp.reached;
            return (
              <div key={g.id} className="border-t border-slate-200 pt-3">
                <div className="flex items-start gap-3">
                  {g.image && (
                    <button onClick={() => setEnlargedGoalImage(g)} className="flex-shrink-0" aria-label="View goal photo" title="View goal photo">
                      <img src={imageSrc(g.image)} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-semibold text-sm text-slate-700 flex items-center gap-1.5">
                        {g.title}
                        <button className="text-slate-300" onClick={() => startEditGoal(g)} title="Edit goal" aria-label="Edit goal">
                          <EditPencilIcon width={14} height={14} />
                        </button>
                      </span>
                      <span className="text-xs text-slate-400">{gp.combined} / {goalTargetTokens(g, rate)} <b className="text-slate-500">({gp.pct}%)</b></span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden flex">
                      <div className={`h-full ${reached ? "bg-emerald-500" : "bg-slate-400"}`} style={{ width: `${gp.tokenPct}%` }} />
                      {gp.vaultPart > 0 && <div className="h-full bg-teal-500" style={{ width: `${gp.pct - gp.tokenPct}%` }} />}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {reached && (() => {
                        const { toggleable } = redeemSourceOptions(child, goalTargetTokens(g, rate), rate);
                        const chosen = payFromOverride[g.id] === "vault" ? "vault" : "token";
                        return (
                          <>
                            {toggleable && (
                              <div className="flex rounded-lg overflow-hidden border border-slate-200">
                                <button type="button" title="Pay from tokens" onClick={() => setPayFromOverride((o) => ({ ...o, [g.id]: "token" }))} className={`px-2 py-0.5 text-xs font-bold ${chosen === "token" ? "bg-slate-800 text-white" : "bg-white text-slate-400"}`}><CoinIcon className="w-3 h-3 inline-block" /></button>
                                <button type="button" title="Pay from Savings Vault" onClick={() => setPayFromOverride((o) => ({ ...o, [g.id]: "vault" }))} className={`px-2 py-0.5 text-xs font-bold ${chosen === "vault" ? "bg-slate-800 text-white" : "bg-white text-slate-400"}`}><VaultIcon className="w-3.5 h-3.5 inline-block" /></button>
                              </div>
                            )}
                            <button className="bg-slate-900 text-white font-bold rounded px-2 py-0.5 text-xs flex items-center gap-1" title="Redeem this goal" onClick={() => redeemGoal(g)}>{ct("kid_detail.redeem_button", "Redeem")}</button>
                          </>
                        );
                      })()}
                      <button className="text-xs text-rose-400 ml-auto" onClick={() => removeGoal(g.id)}>{ct("kid_detail.remove_button", "Remove")}</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <button className="w-full mt-3 bg-emerald-50 text-slate-900 font-bold text-sm rounded-xl py-2.5" onClick={startAddGoal}>{ct("kid_detail.add_goal_button", "+ Add goal")}</button>
      </CollapsibleSection>

      {enlargedGoalImage && <ImageLightbox src={enlargedGoalImage.image} alt={enlargedGoalImage.title} onClose={() => setEnlargedGoalImage(null)} />}

      {goalFormOpen && (
        <GoalFormModal
          editingGoalId={editingGoalId}
          title={goalTitle} onTitleChange={setGoalTitle}
          target={goalTarget} targetPence={goalPence} onTargetChange={(t, p) => { setGoalTarget(t); setGoalPence(p ?? null); }}
          rate={rate}
          image={goalImage} imageError={goalImageError} imageUploading={goalImageUploading}
          onPhotoChange={handleGoalPhoto}
          requirePhoto={goalRequirePhoto} onRequirePhotoChange={setGoalRequirePhoto}
          onSave={saveGoal}
          onClose={() => { setGoalFormOpen(false); setEditingGoalId(null); }}
          headerBg="#0f172a"
          saveBtnClass="w-full bg-slate-900 text-white rounded-lg py-2 text-sm font-bold mt-1"
        />
      )}
    </>
  );
}

// onComplete: set only by the Kids quick-action pop-up, which closes once an activity is added (Alan, 22 Sep).
export function EarningActivitiesSection({ child, state, update, addTransaction, code, open, onToggle, compactClosed, variant, onComplete }) {
  const [activityAddTarget, setActivityAddTarget] = useState(null);
  const [activityEditTarget, setActivityEditTarget] = useState(null);
  const [actLabel, setActLabel] = useState("");
  const [actTokens, setActTokens] = useState("");
  const [actRequirePhoto, setActRequirePhoto] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [editActLabel, setEditActLabel] = useState("");
  const [editActTokens, setEditActTokens] = useState("");
  const [editActRequirePhoto, setEditActRequirePhoto] = useState(false);

  const allActivities = state.activities || [];
  const globalActivities = allActivities.filter((a) => !a.childId);
  const childActivities = allActivities.filter((a) => a.childId === child.id);

  function addActivity() {
    const tokens = parseInt(actTokens, 10);
    if (!actLabel.trim() || !tokens || tokens <= 0) return;
    update((s) => { s.activities.push({ id: uid(), label: actLabel.trim(), tokens, childId: activityAddTarget === "child" ? child.id : null, requirePhoto: actRequirePhoto }); return s; });
    setActLabel(""); setActTokens(""); setActRequirePhoto(false); setActivityAddTarget(null);
    if (onComplete) onComplete();
  }
  function removeActivity(id) { update((s) => { s.activities = s.activities.filter((a) => a.id !== id); return s; }); if (editingActivityId === id) setEditingActivityId(null); }
  function startEditActivity(a) { setEditingActivityId(a.id); setEditActLabel(a.label); setEditActTokens(String(a.tokens)); setEditActRequirePhoto(!!a.requirePhoto); }
  function saveEditActivity() {
    const tokens = parseInt(editActTokens, 10);
    if (!editActLabel.trim() || !tokens || tokens <= 0) return;
    update((s) => { s.activities = s.activities.map((a) => (a.id === editingActivityId ? { ...a, label: editActLabel.trim(), tokens, requirePhoto: editActRequirePhoto } : a)); return s; });
    setEditingActivityId(null);
  }
  // Parent-side instant award: tapping an activity pill (in normal, non-edit mode) credits
  // the ACTIVE child immediately — no request row, no approval step, since the parent IS the
  // approver and is standing right here. Deliberately mirrors what RequestsTab's approve()
  // writes for an earning-activity claim (same reason string, same `earnCap: true` tag so it
  // counts toward the weekly spending-cap progress), so a tap-awarded activity is
  // indistinguishable in Recent activity / undo / cap maths from an approved child claim.
  // A confirm() guards against accidental taps — same native-dialog pattern the delete (×)
  // buttons already use — and requirePhoto is intentionally NOT enforced here: photo proof
  // exists so a parent can verify a CHILD's claim remotely; a parent awarding in person has
  // already verified it themselves.
  function awardActivity(a) {
    if (!window.confirm(`Award "${a.label}" (+${a.tokens} tokens) to ${child.name}?`)) return;
    addTransaction(child.id, a.tokens, a.label, "credit", "token", undefined, { earnCap: true });
  }

  return (
    <CollapsibleSection
      title={ct("kid_detail.activities_title", "Earning activities")}
      icon={variant === "row" ? <Icon id="ic-medal" size={18} hue="gold" /> : undefined}
      open={open}
      onToggle={onToggle}
      compactClosed={compactClosed}
      variant={variant}
      summary={variant === "row" ? (
        <p className="text-xs text-slate-500">{ct("kid_detail.activities_row_summary", "{count} activities{just}", { count: globalActivities.length + childActivities.length, just: childActivities.length > 0 ? ` · ${childActivities.length} just for ${child.name}` : "" })}</p>
      ) : (
        <div>
          <p className="text-base text-slate-500">{ct("kid_detail.activities_intro", "Set up activities that your kids can complete to earn tokens. Some examples are reading books, tidying up, doing their homework.")}</p>
          <p className="text-xs text-slate-400 mt-0.5">{globalActivities.length + childActivities.length} set up</p>
        </div>
      )}
    >
      {/* Only shown when at least one pill exists — a hint about tapping pills is just
          noise on a fresh household with nothing to tap yet. */}
      {(globalActivities.length + childActivities.length) > 0 && (
        <p className="text-xs text-slate-400 mb-2">Tap an activity to award it to {child.name} straight away — tokens are added to their balance instantly, no approval needed.</p>
      )}
      <div className="flex items-center gap-2 mb-1.5">
        <div className="text-xs font-medium text-slate-400">{ct("kid_detail.for_everyone", "For everyone")}</div>
        <button className="text-xs font-bold text-emerald-600" onClick={() => { setActivityAddTarget((t) => (t === "global" ? null : "global")); setActivityEditTarget(null); }}>{activityAddTarget === "global" ? "Cancel" : "+ Add"}</button>
        <button className="text-xs font-bold text-slate-500" onClick={() => { setActivityEditTarget((t) => (t === "global" ? null : "global")); setActivityAddTarget(null); setEditingActivityId(null); }}>{activityEditTarget === "global" ? "Done" : "Edit"}</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-2">
        {globalActivities.map((a) => (
          editingActivityId === a.id ? (
            <div key={a.id} className="border border-slate-200 rounded-xl p-2 bg-white w-full sm:w-48">
              <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.activity_name_label", "Activity Name")}</label>
              <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none mb-1.5" value={editActLabel} onChange={(e) => setEditActLabel(e.target.value)} autoFocus />
              <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.activity_reward_label", "Reward")}</label>
              <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none" type="number" value={editActTokens} onChange={(e) => setEditActTokens(e.target.value)} />
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mt-1.5 cursor-pointer">
                <input type="checkbox" checked={editActRequirePhoto} onChange={(e) => setEditActRequirePhoto(e.target.checked)} />
                {ct("adjust.require_photo", "Require a photo as proof when claimed")} <PremiumChip />
              </label>
              <div className="flex gap-1.5 mt-1.5">
                <button className="text-emerald-700 bg-emerald-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={saveEditActivity}>{ct("kid_detail.save_button", "Save")}</button>
                <button className="text-rose-500 bg-rose-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={() => removeActivity(a.id)}>{ct("kid_detail.delete_button", "Delete")}</button>
              </div>
            </div>
          ) : activityEditTarget === "global" ? (
            <span key={a.id} className="relative inline-flex">
              <button onClick={() => startEditActivity(a)} className="rounded-lg border border-dashed border-slate-300 bg-white text-slate-600 text-xs font-bold px-3 py-1.5 pr-5">
                <EditPencilIcon width={11} height={11} className="inline-block align-[-1px] mr-0.5" /> {a.label} +{a.tokens}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${a.label}"?`)) removeActivity(a.id); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center shadow-md ring-2 ring-white leading-none"
                aria-label={`Delete ${a.label}`}
              >×</button>
            </span>
          ) : (
            <button key={a.id} data-testid="award-activity-pill" aria-label={`Award ${a.label}`} onClick={() => awardActivity(a)} className="rounded-lg border text-xs font-bold px-3 py-1.5 bg-emerald-50 border-emerald-200 text-emerald-700 active:bg-emerald-100">
              {a.label} +{a.tokens}
            </button>
          )
        ))}
      </div>
      {activityAddTarget === "global" && (
        <div className="border border-slate-200 rounded-xl p-2 bg-white w-full sm:w-48 mb-3">
          <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.activity_name_label", "Activity Name")}</label>
          <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none mb-1.5" value={actLabel} onChange={(e) => setActLabel(e.target.value)} />
          <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.activity_reward_label", "Reward")}</label>
          <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none" type="number" value={actTokens} onChange={(e) => setActTokens(e.target.value)} />
          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mt-1.5 cursor-pointer">
            <input type="checkbox" checked={actRequirePhoto} onChange={(e) => setActRequirePhoto(e.target.checked)} />
            {ct("adjust.require_photo", "Require a photo as proof when claimed")} <PremiumChip />
          </label>
          <div className="flex gap-1.5 mt-1.5">
            <button className="text-emerald-700 bg-emerald-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={addActivity}>{ct("kid_detail.add_button", "Add")}</button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 mb-1.5">
        <div className="text-xs font-medium text-slate-400">Just for {child.name}</div>
        <button className="text-xs font-bold text-emerald-600" onClick={() => { setActivityAddTarget((t) => (t === "child" ? null : "child")); setActivityEditTarget(null); }}>{activityAddTarget === "child" ? "Cancel" : "+ Add"}</button>
        <button className="text-xs font-bold text-slate-500" onClick={() => { setActivityEditTarget((t) => (t === "child" ? null : "child")); setActivityAddTarget(null); setEditingActivityId(null); }}>{activityEditTarget === "child" ? "Done" : "Edit"}</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {childActivities.map((a) => (
          editingActivityId === a.id ? (
            <div key={a.id} className="border border-slate-200 rounded-xl p-2 bg-white w-full sm:w-48">
              <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.activity_name_label", "Activity Name")}</label>
              <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none mb-1.5" value={editActLabel} onChange={(e) => setEditActLabel(e.target.value)} autoFocus />
              <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.activity_reward_label", "Reward")}</label>
              <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none" type="number" value={editActTokens} onChange={(e) => setEditActTokens(e.target.value)} />
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mt-1.5 cursor-pointer">
                <input type="checkbox" checked={editActRequirePhoto} onChange={(e) => setEditActRequirePhoto(e.target.checked)} />
                {ct("adjust.require_photo", "Require a photo as proof when claimed")} <PremiumChip />
              </label>
              <div className="flex gap-1.5 mt-1.5">
                <button className="text-emerald-700 bg-emerald-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={saveEditActivity}>{ct("kid_detail.save_button", "Save")}</button>
                <button className="text-rose-500 bg-rose-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={() => removeActivity(a.id)}>{ct("kid_detail.delete_button", "Delete")}</button>
              </div>
            </div>
          ) : activityEditTarget === "child" ? (
            <span key={a.id} className="relative inline-flex">
              <button onClick={() => startEditActivity(a)} className="rounded-lg border border-dashed border-slate-300 bg-white text-slate-600 text-xs font-bold px-3 py-1.5 pr-5">
                <EditPencilIcon width={11} height={11} className="inline-block align-[-1px] mr-0.5" /> {a.label} +{a.tokens}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${a.label}"?`)) removeActivity(a.id); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center shadow-md ring-2 ring-white leading-none"
                aria-label={`Delete ${a.label}`}
              >×</button>
            </span>
          ) : (
            <button key={a.id} data-testid="award-activity-pill" aria-label={`Award ${a.label}`} onClick={() => awardActivity(a)} className="rounded-lg border text-xs font-bold px-3 py-1.5 bg-emerald-50 border-emerald-200 text-emerald-700 active:bg-emerald-100">
              {a.label} +{a.tokens}
            </button>
          )
        ))}
      </div>
      {activityAddTarget === "child" && (
        <div className="border border-slate-200 rounded-xl p-2 bg-white w-full sm:w-48 mt-2">
          <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.activity_name_label", "Activity Name")}</label>
          <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none mb-1.5" value={actLabel} onChange={(e) => setActLabel(e.target.value)} />
          <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.activity_reward_label", "Reward")}</label>
          <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none" type="number" value={actTokens} onChange={(e) => setActTokens(e.target.value)} />
          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mt-1.5 cursor-pointer">
            <input type="checkbox" checked={actRequirePhoto} onChange={(e) => setActRequirePhoto(e.target.checked)} />
            {ct("adjust.require_photo", "Require a photo as proof when claimed")} <PremiumChip />
          </label>
          <div className="flex gap-1.5 mt-1.5">
            <button className="text-emerald-700 bg-emerald-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={addActivity}>{ct("kid_detail.add_button", "Add")}</button>
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
}

// `setupMode` (staging bug 4, 30 Aug 2026): mounted inside the setup wizard, where a
// household that was created minutes ago cannot possibly have a missed day to repair. The
// streak-repair block was rendering there anyway — an offer to fix history that does not
// exist yet, on the screen where a parent is least equipped to understand it.
export function DailyHabitsSection({ child, state, update, open, onToggle, compactClosed, variant, setupMode = false }) {
  const [habitAddTarget, setHabitAddTarget] = useState(null);
  const [habitLabel, setHabitLabel] = useState("");
  const [habitRequirePhoto, setHabitRequirePhoto] = useState(false);
  const [habitEditTarget, setHabitEditTarget] = useState(null);
  const [editingHabitId, setEditingHabitId] = useState(null);
  const [editHabitLabel, setEditHabitLabel] = useState("");
  const [editHabitRequirePhoto, setEditHabitRequirePhoto] = useState(false);

  const allHabits = state.habits || [];
  const globalHabits = allHabits.filter((h) => !h.childId);
  const childHabits = allHabits.filter((h) => h.childId === child.id);
  const childStreak = currentHabitStreak(state.habitLog || [], allHabits, child.id);
  const childWeek = habitWeekProgress(state.habitLog || [], allHabits, child.id);

  // ---- W4 streak repair (workplan: 1 per child per week, PAST days only) ----
  // Life happens: a genuinely-done day that never got ticked shouldn't cost the streak.
  // The repair marks every applicable task done for ONE past day of the current week.
  // Hard rules, each enforced BOTH here and re-checked inside the updater (rebase-safe):
  // past days only (never today — today is tickable normally), current week only, not
  // after the week's reward is approved (locked), and once per child per week.
  const [repairConfirm, setRepairConfirm] = useState(null); // dateStr | null
  const repairWeekStart = childWeek.weekStart;
  const repairUsed = child.lastStreakRepairWeek === repairWeekStart;
  const weekRewardLocked = (state.requests || []).some((r) => r.requestType === "habit_reward" && r.childId === child.id && r.weekStart === repairWeekStart && r.status === "approved");
  const repairableDays = (() => {
    const days = [];
    const today = todayStr();
    const d = new Date(repairWeekStart + "T00:00:00");
    for (let i = 0; i < 7; i++) {
      const ds = d.toISOString().slice(0, 10);
      if (ds >= today) break; // past days only
      days.push({ date: ds, complete: isHabitDayComplete(state.habitLog || [], allHabits, child.id, ds) });
      d.setDate(d.getDate() + 1);
    }
    return days;
  })();
  function repairDay(dateStr) {
    update((s) => {
      const c = s.profiles.find((p) => p.id === child.id);
      if (!c) return null;
      const ws = weekStartOf(dateStr);
      // Re-verify every rule against FRESH state — this updater may replay in a rebase.
      if (ws !== weekStartOf(todayStr())) return null;           // current week only
      if (dateStr >= todayStr()) return null;                    // past days only
      if (c.lastStreakRepairWeek === ws) return null;            // one per week
      if ((s.requests || []).some((r) => r.requestType === "habit_reward" && r.childId === c.id && r.weekStart === ws && r.status === "approved")) return null; // locked
      const applicable = habitsForChild(s.habits || [], c.id);
      if (applicable.length === 0) return null;
      let added = false;
      for (const h of applicable) {
        const already = (s.habitLog || []).some((l) => l.childId === c.id && l.taskId === h.id && l.date === dateStr);
        if (!already) { s.habitLog.push({ id: uid(), childId: c.id, taskId: h.id, date: dateStr, repaired: true }); added = true; }
      }
      if (!added) return null;
      c.lastStreakRepairWeek = ws;
      // Same threshold check as toggleHabit: if the repaired day tips the week over the
      // line and nothing's been requested yet, raise the (nudge-framed) weekly request.
      const { weekStart: wsNow, completeDays } = habitWeekProgress(s.habitLog, s.habits, c.id, dateStr);
      const threshold = c.habitThreshold || 7;
      const reward = c.habitReward || 20;
      if (completeDays >= threshold) raiseHabitReward(s, c, wsNow, completeDays, threshold, reward, getDeviceId()); // LB-13: shared raise, idempotent
      return s;
    });
    setRepairConfirm(null);
  }

  function addHabit() {
    if (!habitLabel.trim()) return;
    update((s) => {
      if (!s.habits) s.habits = [];
      s.habits.push({ id: uid(), label: habitLabel.trim(), childId: habitAddTarget === "child" ? child.id : null, requirePhoto: habitRequirePhoto });
      return s;
    });
    setHabitLabel(""); setHabitRequirePhoto(false); setHabitAddTarget(null);
  }
  function removeHabit(id) { update((s) => { s.habits = (s.habits || []).filter((h) => h.id !== id); return s; }); if (editingHabitId === id) setEditingHabitId(null); }
  function startEditHabit(h) { setEditingHabitId(h.id); setEditHabitLabel(h.label); setEditHabitRequirePhoto(!!h.requirePhoto); }
  function saveEditHabit() {
    if (!editHabitLabel.trim()) return;
    update((s) => { s.habits = s.habits.map((h) => (h.id === editingHabitId ? { ...h, label: editHabitLabel.trim(), requirePhoto: editHabitRequirePhoto } : h)); return s; });
    setEditingHabitId(null);
  }
  // Whether the reward/threshold fields below apply to every child (checked, the default) or
  // just the one currently on screen (unchecked). Stored at household level, not per-child,
  // since it's a single shared switch — see toggleApplyHabitToAll for what happens on flip.
  const applyToAll = state.settings.applyHabitToAll ?? true;
  function setHabitReward(valStr) {
    const val = Math.max(1, parseInt(valStr, 10) || 1);
    update((s) => {
      if (s.settings.applyHabitToAll ?? true) {
        s.settings.defaultHabitReward = val;
        s.profiles.forEach((p) => { p.habitReward = val; });
      } else {
        const c = s.profiles.find((p) => p.id === child.id);
        if (c) c.habitReward = val;
      }
      return s;
    });
  }
  function setHabitThreshold(valStr) {
    const val = Math.min(7, Math.max(1, parseInt(valStr, 10) || 1));
    update((s) => {
      if (s.settings.applyHabitToAll ?? true) {
        s.settings.defaultHabitThreshold = val;
        s.profiles.forEach((p) => { p.habitThreshold = val; });
      } else {
        const c = s.profiles.find((p) => p.id === child.id);
        if (c) c.habitThreshold = val;
      }
      return s;
    });
  }
  // Turning the checkbox OFF takes no action beyond flipping the flag — every child keeps
  // whatever values they currently have, they just become independently editable from here on.
  // Turning it ON takes whatever's shown on screen right now (this child's own values, since
  // the fields were independent a moment ago) and pushes that to every child, so "apply to all"
  // genuinely means all of them match immediately rather than just the next child someone edits.
  function toggleApplyHabitToAll() {
    update((s) => {
      const was = s.settings.applyHabitToAll ?? true;
      if (was) {
        s.settings.applyHabitToAll = false;
      } else {
        const c = s.profiles.find((p) => p.id === child.id);
        const reward = c?.habitReward ?? 20;
        const threshold = c?.habitThreshold ?? 7;
        s.settings.defaultHabitReward = reward;
        s.settings.defaultHabitThreshold = threshold;
        s.profiles.forEach((p) => { p.habitReward = reward; p.habitThreshold = threshold; });
        s.settings.applyHabitToAll = true;
      }
      return s;
    });
  }

  return (
    <CollapsibleSection
      title={ct("kid_detail.habits_title", "Daily habits and streaks")}
      icon={variant === "row" ? <Icon id="ic-flame" size={18} hue="gold" /> : undefined}
      open={open}
      onToggle={onToggle}
      compactClosed={compactClosed}
      variant={variant}
      summary={variant === "row" ? (
        <p className="text-xs text-slate-500">{ct("kid_detail.habits_row_summary", "{count} habits · {done}/{target} this week · {streak}-day streak", { count: globalHabits.length + childHabits.length, done: childWeek.completeDays, target: child.habitThreshold || 7, streak: childStreak })}</p>
      ) : (
        <div>
          <p className="text-base text-slate-500">{ct("kid_detail.habits_intro", "Tell us how many days per week your child needs to complete their activity list to get a weekly reward and how much to reward them.")}</p>
          <p className="text-xs text-slate-400 mt-0.5">{childWeek.completeDays}/{child.habitThreshold || 7} this week · <Icon id="ic-flame" size={12} hue="amber" className="inline-block align-[-2px]" /> {childStreak}</p>
        </div>
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <div className="flex items-start gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-400 w-14 leading-tight flex-shrink-0">{ct("kid_detail.habit_reward_label", "Reward (tokens)")}</label>
            <input type="number" min="1" className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm w-14 text-center flex-shrink-0"
              key={`reward-${applyToAll ? "all" : child.id}-${applyToAll ? state.settings.defaultHabitReward : child.habitReward}`}
              defaultValue={applyToAll ? (state.settings.defaultHabitReward ?? 20) : (child.habitReward ?? 20)}
              onBlur={(e) => setHabitReward(e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-slate-400 w-24 leading-tight flex-shrink-0">{ct("kid_detail.habit_days_label", "Days/week to earn a reward")}</label>
            <input type="number" min="1" max="7" className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm w-14 text-center flex-shrink-0"
              key={`thresh-${applyToAll ? "all" : child.id}-${applyToAll ? state.settings.defaultHabitThreshold : child.habitThreshold}`}
              defaultValue={applyToAll ? (state.settings.defaultHabitThreshold ?? 7) : (child.habitThreshold ?? 7)}
              onBlur={(e) => setHabitThreshold(e.target.value)} />
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-medium text-slate-400">{ct("kid_detail.habits_this_week", "This week")}</div>
          <div className="text-sm font-semibold text-slate-700 mt-0.5">{childWeek.completeDays}/{child.habitThreshold || 7} days · <Icon id="ic-flame" size={14} hue="amber" className="inline-block align-[-2px]" /> {childStreak}</div>
        </div>
      </div>

      {/* LB-13 (W2.6): household switch for how the weekly reward pays out. Hidden in the
          wizard (setupMode) — the default is the decision there; a parent who wants manual
          approval finds it here, beside the amount and threshold it governs. Title and
          switch share one row and the description sits below at full width, so flipping
          the switch changes only the words, never the layout (Alan, staging pass 8 Sep). */}
      {!setupMode && (() => {
        const mode = state.settings.habitRewardMode === "auto" ? "auto" : "manual";
        function setMode(next) {
          if (next === mode) return;
          update((s) => { logSetting(s, getDeviceId(), "habitRewardMode", mode, next, actor(s)); s.settings.habitRewardMode = next; return s; });
        }
        const btn = (value, label) => (
          <button type="button" role="radio" aria-checked={mode === value} data-testid={`habit-reward-mode-${value}`} onClick={() => setMode(value)}
            className={`px-3 py-1.5 text-xs font-bold ${mode === value ? "bg-navy text-white" : "bg-white text-slate-600"}`}>{label}</button>
        );
        return (
          <div className="mb-3" data-testid="habit-reward-mode">
            <div className="flex items-center gap-3">
              <div className="text-xs font-semibold text-slate-700">{ct("kid_detail.habit_reward_mode.title", "Weekly reward")}</div>
              <div className="flex rounded-lg overflow-hidden border border-slate-200 flex-shrink-0" role="radiogroup" aria-label={ct("kid_detail.habit_reward_mode.label", "How the weekly reward is paid")}>
                {btn("auto", ct("kid_detail.habit_reward_mode.auto", "Automatic"))}
                {btn("manual", ct("kid_detail.habit_reward_mode.manual", "Manual"))}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1">{mode === "auto"
              ? ct("kid_detail.habit_reward_mode.auto_body", "Paid automatically when the week is done, within the spending cap.")
              : ct("kid_detail.habit_reward_mode.manual_body", "Not paid until you approve it. Each week comes to you as a request.")}</p>
          </div>
        );
      })()}

      {/* W4 streak repair — shown only when there's something to repair and the tools to
          do it (tasks exist, week not locked). One per child per week; past days only. */}
      {!setupMode && repairableDays.some((d) => !d.complete) && !weekRewardLocked && habitsForChild(allHabits, child.id).length > 0 && (
        <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 mb-3" data-testid="streak-repair">
          <div className="text-xs font-semibold text-slate-700 mb-1">{ct("kid_detail.repair_title", "Streak repair")}</div>
          <p className="text-xs text-slate-500 mb-2">{repairUsed ? ct("kid_detail.repair_used", "This week's repair has been used — everyone gets one per week.") : ct("kid_detail.repair_sub", "Did a day get done but never ticked? Mark one past day of this week as done. You can do this once a week.")}</p>
          {!repairUsed && (
            <div className="flex flex-wrap gap-1.5">
              {repairableDays.map((d) => {
                const label = new Date(d.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short" });
                if (d.complete) return <span key={d.date} className="text-xs font-bold px-2 py-1 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100"><span className="inline-flex items-center gap-1">{label}<Icon id="ic-check" size={12} style={{ color: "currentColor" }} /></span></span>;
                return repairConfirm === d.date ? (
                  <span key={d.date} className="inline-flex items-center gap-1">
                    <button className="text-xs font-bold px-2 py-1 rounded-lg bg-slate-800 text-white" onClick={() => repairDay(d.date)}>{ct("kid_detail.repair_confirm", "Repair {day}?", { day: label })}</button>
                    <button className="text-xs font-bold px-2 py-1 rounded-lg bg-white border border-slate-200 text-slate-500" aria-label="Cancel" onClick={() => setRepairConfirm(null)}><Icon id="ic-close" size={12} className="inline-block" style={{ color: "currentColor" }} /></button>
                  </span>
                ) : (
                  <button key={d.date} className="text-xs font-bold px-2 py-1 rounded-lg bg-white border border-slate-200 text-slate-500" data-testid={`repair-day-${d.date}`} onClick={() => setRepairConfirm(d.date)}>{label} +</button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* W1.15 (Aug 2026): with a single child in the household, "apply to all kids" is a
          distinction without a difference — hide the switch entirely rather than make one
          parent puzzle over it. Same >1-children-total predicate as the child picker
          (visibility ignored); applyHabitToAll stays at its default (true) untouched, so
          a second child added later inherits the household defaults exactly as before.
          The "For everyone" / "Just for {name}" habit-list split below deliberately stays
          even for one child: a "For everyone" habit auto-applies to any sibling added
          later, which is a real difference worth keeping visible. */}
      {state.profiles.length > 1 && (
        <label className="flex items-center gap-2 mb-4 text-xs font-medium text-slate-600 cursor-pointer">
          <input type="checkbox" checked={applyToAll} onChange={toggleApplyHabitToAll} />
          Apply these settings to all kids
        </label>
      )}

      <div className="flex items-center gap-2 mb-1.5">
        <div className="text-xs font-medium text-slate-400">{ct("kid_detail.for_everyone", "For everyone")}</div>
        <button className="text-xs font-bold text-emerald-600" onClick={() => { setHabitAddTarget((t) => (t === "global" ? null : "global")); setHabitEditTarget(null); }}>{habitAddTarget === "global" ? "Cancel" : "+ Add"}</button>
        <button className="text-xs font-bold text-slate-500" onClick={() => { setHabitEditTarget((t) => (t === "global" ? null : "global")); setHabitAddTarget(null); setEditingHabitId(null); }}>{habitEditTarget === "global" ? "Done" : "Edit"}</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-2">
        {globalHabits.map((h) => (
          editingHabitId === h.id ? (
            <div key={h.id} className="border border-slate-200 rounded-xl p-2 bg-white w-full sm:w-48">
              <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.task_label", "Task")}</label>
              <div className="flex gap-2 mb-1.5">
                <input className="text-xs font-bold flex-1 border-b border-slate-200 focus:outline-none" value={editHabitLabel} onChange={(e) => setEditHabitLabel(e.target.value)} autoFocus />
              </div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 cursor-pointer">
                <input type="checkbox" checked={editHabitRequirePhoto} onChange={(e) => setEditHabitRequirePhoto(e.target.checked)} />
                Require a photo when checked off
              </label>
              <div className="flex gap-1.5 mt-1.5">
                <button className="text-emerald-700 bg-emerald-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={saveEditHabit}>{ct("kid_detail.save_button", "Save")}</button>
                <button className="text-rose-500 bg-rose-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={() => removeHabit(h.id)}>{ct("kid_detail.delete_button", "Delete")}</button>
              </div>
            </div>
          ) : habitEditTarget === "global" ? (
            <span key={h.id} className="relative inline-flex">
              <button onClick={() => startEditHabit(h)} className="rounded-lg border border-dashed border-slate-300 bg-white text-slate-600 text-xs font-bold px-3 py-1.5 pr-5">
                <EditPencilIcon width={11} height={11} className="inline-block align-[-1px] mr-0.5" /> {h.label}{h.requirePhoto ? <Icon id="ic-photo" size={12} className="inline-block align-[-2px] ml-1" /> : ""}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${h.label}"?`)) removeHabit(h.id); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center shadow-md ring-2 ring-white leading-none"
                aria-label={`Delete ${h.label}`}
              >×</button>
            </span>
          ) : (
            <span key={h.id} className="rounded-lg border text-xs font-medium px-3 py-1.5 bg-slate-50 border-slate-200 text-slate-600">
              {h.label}{h.requirePhoto ? <Icon id="ic-photo" size={12} className="inline-block align-[-2px] ml-1" /> : ""}
            </span>
          )
        ))}
        {globalHabits.length === 0 && <span className="text-xs text-slate-300">{ct("kid_detail.none_yet", "None yet")}</span>}
      </div>
      {habitAddTarget === "global" && (
        <div className="mb-3">
          <div className="flex gap-2">
            <input className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs flex-1" placeholder="Task (e.g. Make bed)" value={habitLabel} onChange={(e) => setHabitLabel(e.target.value)} />
            <button className="bg-slate-900 text-white rounded-lg px-3 text-xs font-bold" onClick={addHabit}>{ct("kid_detail.add_button", "Add")}</button>
          </div>
          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mt-1.5 cursor-pointer">
            <input type="checkbox" checked={habitRequirePhoto} onChange={(e) => setHabitRequirePhoto(e.target.checked)} />
            Require a photo when checked off
          </label>
          <p className="text-xs text-slate-400 mt-2">A day only counts toward the streak once every task on {child.name}'s list is checked off.</p>
        </div>
      )}

      <div className="flex items-center gap-2 mb-1.5">
        <div className="text-xs font-medium text-slate-400">Just for {child.name}</div>
        <button className="text-xs font-bold text-emerald-600" onClick={() => { setHabitAddTarget((t) => (t === "child" ? null : "child")); setHabitEditTarget(null); }}>{habitAddTarget === "child" ? "Cancel" : "+ Add"}</button>
        <button className="text-xs font-bold text-slate-500" onClick={() => { setHabitEditTarget((t) => (t === "child" ? null : "child")); setHabitAddTarget(null); setEditingHabitId(null); }}>{habitEditTarget === "child" ? "Done" : "Edit"}</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {childHabits.map((h) => (
          editingHabitId === h.id ? (
            <div key={h.id} className="border border-slate-200 rounded-xl p-2 bg-white w-full sm:w-48">
              <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.task_label", "Task")}</label>
              <div className="flex gap-2 mb-1.5">
                <input className="text-xs font-bold flex-1 border-b border-slate-200 focus:outline-none" value={editHabitLabel} onChange={(e) => setEditHabitLabel(e.target.value)} autoFocus />
              </div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 cursor-pointer">
                <input type="checkbox" checked={editHabitRequirePhoto} onChange={(e) => setEditHabitRequirePhoto(e.target.checked)} />
                Require a photo when checked off
              </label>
              <div className="flex gap-1.5 mt-1.5">
                <button className="text-emerald-700 bg-emerald-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={saveEditHabit}>{ct("kid_detail.save_button", "Save")}</button>
                <button className="text-rose-500 bg-rose-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={() => removeHabit(h.id)}>{ct("kid_detail.delete_button", "Delete")}</button>
              </div>
            </div>
          ) : habitEditTarget === "child" ? (
            <span key={h.id} className="relative inline-flex">
              <button onClick={() => startEditHabit(h)} className="rounded-lg border border-dashed border-slate-300 bg-white text-slate-600 text-xs font-bold px-3 py-1.5 pr-5">
                <EditPencilIcon width={11} height={11} className="inline-block align-[-1px] mr-0.5" /> {h.label}{h.requirePhoto ? <Icon id="ic-photo" size={12} className="inline-block align-[-2px] ml-1" /> : ""}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${h.label}"?`)) removeHabit(h.id); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center shadow-md ring-2 ring-white leading-none"
                aria-label={`Delete ${h.label}`}
              >×</button>
            </span>
          ) : (
            <span key={h.id} className="rounded-lg border text-xs font-medium px-3 py-1.5 bg-slate-50 border-slate-200 text-slate-600">
              {h.label}{h.requirePhoto ? <Icon id="ic-photo" size={12} className="inline-block align-[-2px] ml-1" /> : ""}
            </span>
          )
        ))}
        {childHabits.length === 0 && <span className="text-xs text-slate-300">{ct("kid_detail.none_yet", "None yet")}</span>}
      </div>
      {habitAddTarget === "child" && (
        <div className="mt-2">
          <div className="flex gap-2">
            <input className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs flex-1" placeholder="Task (e.g. Make bed)" value={habitLabel} onChange={(e) => setHabitLabel(e.target.value)} />
            <button className="bg-slate-900 text-white rounded-lg px-3 text-xs font-bold" onClick={addHabit}>{ct("kid_detail.add_button", "Add")}</button>
          </div>
          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mt-1.5 cursor-pointer">
            <input type="checkbox" checked={habitRequirePhoto} onChange={(e) => setHabitRequirePhoto(e.target.checked)} />
            Require a photo when checked off
          </label>
          <p className="text-xs text-slate-400 mt-2">A day only counts toward the streak once every task on {child.name}'s list is checked off.</p>
        </div>
      )}
    </CollapsibleSection>
  );
}
